
import { supabase } from "@/integrations/supabase/client";
import { Agent, AgentFormData, AgentWithVariables, AgentVariable, AgentVersion } from "@/types/admin";
import { updateElevenLabsAgent } from "@/services/ai/elevenLabsService";

export const fetchAgents = async (): Promise<Agent[]> => {
  try {
    const { data, error } = await supabase
      .from("agents")
      .select("*")
      .order("created_at", { ascending: false });

    if (error) {
      throw error;
    }

    return data || [];
  } catch (error) {
    console.error("Error fetching agents:", error);
    return [];
  }
};

export const fetchAgentWithVariables = async (agentId: string): Promise<AgentWithVariables | null> => {
  try {
    const { data: agent, error: agentError } = await supabase
      .from("agents")
      .select("*")
      .eq("id", agentId)
      .single();

    if (agentError) {
      throw agentError;
    }

    const { data: variables, error: variablesError } = await supabase
      .from("agent_variables")
      .select("*")
      .eq("agent_id", agentId);

    if (variablesError) {
      throw variablesError;
    }

    return { ...agent, variables: variables || [] };
  } catch (error) {
    console.error("Error fetching agent with variables:", error);
    return null;
  }
};

export const fetchAgentByElevenLabsId = async (elevenLabsAgentId: string): Promise<AgentWithVariables | null> => {
  try {
    const { data: agent, error: agentError } = await supabase
      .from("agents")
      .select("*")
      .eq("elevenlabs_agent_id", elevenLabsAgentId)
      .single();

    if (agentError) {
      throw agentError;
    }

    const { data: variables, error: variablesError } = await supabase
      .from("agent_variables")
      .select("*")
      .eq("agent_id", agent.id);

    if (variablesError) {
      throw variablesError;
    }

    return { ...agent, variables: variables || [] };
  } catch (error) {
    console.error("Error fetching agent by ElevenLabs ID:", error);
    return null;
  }
};

export const createOrSyncAgent = async (data: AgentFormData): Promise<{ data: Agent | null; error: any }> => {
  try {
    if (!data.elevenlabs_agent_id) {
      throw new Error("ElevenLabs agent ID is required");
    }

    const { data: existingAgent, error: findError } = await supabase
      .from("agents")
      .select("*")
      .eq("elevenlabs_agent_id", data.elevenlabs_agent_id)
      .single();

    if (findError && findError.code !== 'PGRST116') {
      throw findError;
    }

    if (existingAgent) {
      const { data: updatedAgent, error: updateError } = await supabase
        .from("agents")
        .update(data)
        .eq("id", existingAgent.id)
        .select()
        .single();

      if (updateError) {
        throw updateError;
      }

      return { data: updatedAgent, error: null };
    } else {
      const { data: newAgent, error: createError } = await supabase
        .from("agents")
        .insert([data])
        .select()
        .single();

      if (createError) {
        throw createError;
      }

      await createAgentVersionSnapshot(newAgent.id, newAgent);

      return { data: newAgent, error: null };
    }
  } catch (error: any) {
    console.error("Error creating/syncing agent:", error);
    return { data: null, error: error.message || "An unexpected error occurred" };
  }
};

const createAgentVersionSnapshot = async (
  agentId: string, 
  agent: Agent, 
  edited_by: string | null = null
): Promise<boolean> => {
  try {
    const { data: versions, error: versionsError } = await supabase
      .from("agent_versions")
      .select("version_number")
      .eq("agent_id", agentId)
      .order("version_number", { ascending: false })
      .limit(1);

    if (versionsError) {
      throw versionsError;
    }

    const nextVersionNumber = versions && versions.length > 0 
      ? versions[0].version_number + 1 
      : 1;

    const settingsSnapshot = {
      name: agent.name,
      description: agent.description,
      voice_id: agent.voice_id,
      language: agent.language,
      first_message: agent.first_message,
      max_duration_seconds: agent.max_duration_seconds,
      goal: agent.goal,
      stability: agent.stability,
      speed: agent.speed,
      similarity_boost: agent.similarity_boost,
      optimize_latency: agent.optimize_latency,
      elevenlabs_agent_id: agent.elevenlabs_agent_id,
      phone_number: agent.phone_number,
      phone_number_id: agent.phone_number_id,
    };

    const { error: insertError } = await supabase
      .from("agent_versions")
      .insert({
        agent_id: agentId,
        version_number: nextVersionNumber,
        edited_by,
        settings_snapshot: settingsSnapshot,
      });

    if (insertError) {
      throw insertError;
    }

    return true;
  } catch (error) {
    console.error("Error creating agent version snapshot:", error);
    return false;
  }
};

export const createAgent = async (data: AgentFormData): Promise<{ data: Agent | null; error: any }> => {
  try {
    const { data: newAgent, error } = await supabase
      .from("agents")
      .insert([data])
      .select()
      .single();

    if (error) {
      console.error("Error creating agent:", error);
      return { data: null, error };
    }

    await createAgentVersionSnapshot(newAgent.id, newAgent);

    return { data: newAgent, error: null };
  } catch (error: any) {
    console.error("Error creating agent:", error);
    return { data: null, error: error.message || "An unexpected error occurred" };
  }
};

export const updateAgent = async (
  id: string, 
  data: Partial<AgentFormData>, 
  edited_by: string | null = null
): Promise<{ data: Agent | null; error: any }> => {
  try {
    const { data: currentAgent, error: fetchError } = await supabase
      .from("agents")
      .select("*")
      .eq("id", id)
      .single();
    
    if (fetchError) {
      console.error("Error fetching current agent state:", fetchError);
      return { data: null, error: fetchError };
    }

    const { data: updatedAgent, error } = await supabase
      .from("agents")
      .update(data)
      .eq("id", id)
      .select()
      .single();

    if (error) {
      console.error("Error updating agent in database:", error);
      return { data: null, error };
    }

    await createAgentVersionSnapshot(id, { ...currentAgent, ...data }, edited_by);

    if (updatedAgent.elevenlabs_agent_id) {
      console.log("Syncing agent changes to ElevenLabs:", updatedAgent.elevenlabs_agent_id);

      const elevenLabsUpdateData: any = {};
      
      if (data.name !== undefined) {
        elevenLabsUpdateData.name = data.name;
      }
      
      if (data.description !== undefined) {
        elevenLabsUpdateData.description = data.description;
      }

      if (data.voice_id !== undefined) {
        elevenLabsUpdateData.voice_id = data.voice_id;
      }

      if (data.language !== undefined) {
        elevenLabsUpdateData.language = data.language;
      }

      if (data.first_message !== undefined) {
        elevenLabsUpdateData.first_message = data.first_message;
      }

      if (data.max_duration_seconds !== undefined) {
        elevenLabsUpdateData.max_duration_seconds = data.max_duration_seconds;
      }

      if (
        data.stability !== undefined ||
        data.similarity_boost !== undefined ||
        data.speed !== undefined ||
        data.optimize_latency !== undefined
      ) {
        elevenLabsUpdateData.tts_settings = {};
        
        if (data.stability !== undefined) {
          elevenLabsUpdateData.tts_settings.stability = data.stability;
        }
        
        if (data.similarity_boost !== undefined) {
          elevenLabsUpdateData.tts_settings.similarity_boost = data.similarity_boost;
        }
        
        if (data.speed !== undefined) {
          elevenLabsUpdateData.tts_settings.speed = data.speed;
        }
        
        if (data.optimize_latency !== undefined) {
          elevenLabsUpdateData.tts_settings.optimize_streaming_latency = data.optimize_latency ? 1 : 0;
        }
      }

      if (Object.keys(elevenLabsUpdateData).length > 0) {
        console.log("Sending these changes to ElevenLabs:", JSON.stringify(elevenLabsUpdateData, null, 2));
        
        try {
          const elevenLabsResult = await updateElevenLabsAgent(
            updatedAgent.elevenlabs_agent_id,
            elevenLabsUpdateData
          );

          if (!elevenLabsResult.success) {
            console.error("Error updating agent in ElevenLabs:", elevenLabsResult.error);
            return { 
              data: updatedAgent, 
              error: { 
                databaseSuccess: true, 
                elevenLabsError: elevenLabsResult.error,
                details: elevenLabsResult.details
              }
            };
          } else {
            console.log("Successfully updated agent in ElevenLabs");
          }
        } catch (updateError) {
          console.error("Exception during ElevenLabs update:", updateError);
          return { 
            data: updatedAgent, 
            error: { 
              databaseSuccess: true, 
              elevenLabsError: updateError instanceof Error ? updateError.message : "Unknown error during ElevenLabs update"
            }
          };
        }
      } else {
        console.log("No changes to sync with ElevenLabs");
      }
    }

    return { data: updatedAgent, error: null };
  } catch (error: any) {
    console.error("Error updating agent:", error);
    return { data: null, error: error.message || "An unexpected error occurred" };
  }
};

export const deleteAgent = async (id: string): Promise<{ success: boolean; error: any }> => {
  try {
    const { error: variablesError } = await supabase
      .from("agent_variables")
      .delete()
      .eq("agent_id", id);

    if (variablesError) {
      console.error("Error deleting agent variables:", variablesError);
      return { success: false, error: variablesError };
    }

    const { error: agentError } = await supabase
      .from("agents")
      .delete()
      .eq("id", id);

    if (agentError) {
      console.error("Error deleting agent:", agentError);
      return { success: false, error: agentError };
    }

    return { success: true, error: null };
  } catch (error: any) {
    console.error("Error deleting agent:", error);
    return { success: false, error: error.message || "An unexpected error occurred" };
  }
};

export const fetchAgentVersions = async (agentId: string): Promise<AgentVersion[]> => {
  try {
    const { data, error } = await supabase
      .from("agent_versions")
      .select("*")
      .eq("agent_id", agentId)
      .order("version_number", { ascending: false });

    if (error) {
      throw error;
    }

    return data || [];
  } catch (error) {
    console.error("Error fetching agent versions:", error);
    return [];
  }
};

export const rollbackToVersion = async (agentId: string, versionId: string): Promise<{ success: boolean; error: any }> => {
  try {
    const { data: version, error: versionError } = await supabase
      .from("agent_versions")
      .select("*")
      .eq("id", versionId)
      .single();

    if (versionError || !version) {
      throw versionError || new Error("Version not found");
    }

    const { error: updateError } = await supabase
      .from("agents")
      .update(version.settings_snapshot)
      .eq("id", agentId);

    if (updateError) {
      throw updateError;
    }

    const { data: agent, error: agentError } = await supabase
      .from("agents")
      .select("*")
      .eq("id", agentId)
      .single();

    if (agentError || !agent) {
      throw agentError || new Error("Agent not found after rollback");
    }

    const rollbackNote = `Rollback to v${version.version_number}`;
    await createAgentVersionSnapshot(agentId, agent, rollbackNote);

    return { success: true, error: null };
  } catch (error: any) {
    console.error("Error rolling back to version:", error);
    return { success: false, error: error.message || "An unexpected error occurred" };
  }
};

export const fetchElevenLabsAgentDetails = async (agentId: string): Promise<{ 
  success: boolean; 
  agent?: any; 
  error?: string 
}> => {
  try {
    console.log(`Fetching ElevenLabs agent details for ID: ${agentId}`);
    
    const { data, error } = await supabase.functions.invoke("get-elevenlabs-agent", {
      body: { agentId },
    });

    if (error) {
      console.error("Supabase function error:", error);
      throw error;
    }

    if (!data.success) {
      console.error("ElevenLabs API error:", data.error);
      throw new Error(data.error || "Failed to fetch ElevenLabs agent details");
    }

    console.log("Successfully fetched ElevenLabs agent details");
    
    return {
      success: true,
      agent: data.agent,
    };
  } catch (error) {
    console.error("Error fetching ElevenLabs agent details:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "An unknown error occurred",
    };
  }
};
